// Message history transfer when linking a device (HRTH-202). The device making the link
// puts a fresh key in the link's fragment (`t=`, never sent to a server). Once the new
// device has joined its conversations it says so ('ready'), and the old one sends its
// history, then (if asked) the attachments it keeps for those messages, then 'done'. Each
// part goes through the server as ciphertext: its label (`meta`) sealed under the
// transfer key, its bytes as hearth-att v1 under a fresh key carried in the label. The
// server deletes a part once the new device has it, or 10 minutes after its upload.
import { reactive } from 'vue'
import type { TransferPart } from '../api/types'
import type { StoredMessage } from '../storage/db'
import { b64url, decryptAll, fileEncryptor, fromB64url, uploadResumable, type MediaKeyInfo } from './attachments'

export type TransferMeta =
  | { kind: 'ready' }
  | { kind: 'history'; info: MediaKeyInfo }
  | { kind: 'attachment'; info: MediaKeyInfo; media: string; mime: string }
  | { kind: 'done' }

/** The history part's contents. */
export interface HistoryDoc {
  format: 'hrth-transfer/v1'
  messages: StoredMessage[]
  /** Attachments that follow, in order, and their total size (for progress). */
  attachments: string[]
  bytes: number
  /** Attachments of these messages that won't come (not kept on the old device, or not asked for). */
  skipped: string[]
}

export interface TransferApi {
  startTransfer(to: string, size: number, meta: string): Promise<{ id: string; received: number }>
  transferStatus(id: string): Promise<{ received: number }>
  transferPart(id: string, offset: number, part: Blob): Promise<{ received: number }>
  completeTransfer(id: string): Promise<unknown>
  waitingTransfers(): Promise<{ parts: TransferPart[] }>
  transferData(id: string): Promise<ArrayBuffer>
  deleteTransfer(id: string): Promise<void>
}

/** What the old device sends from. */
export interface TransferSource {
  messages(): Promise<StoredMessage[]>
  attachment(id: string): Promise<Blob | null>
}

/** Where the new device puts what arrives. */
export interface TransferSink {
  importMessages(messages: StoredMessage[]): Promise<number>
  keepAttachment(id: string, file: Blob): Promise<void>
  /** Attachments that weren't transferred (shown as such when the server no longer has them). */
  notTransferred(ids: string[]): Promise<void>
}

export type TransferPhase = 'waiting' | 'history' | 'attachments' | 'done' | 'failed' | 'stopped'

export interface TransferProgress {
  role: 'send' | 'receive' | null
  phase: TransferPhase
  /** Bytes moved of `total` (0 until the history part is under way). */
  done: number
  total: number
  messages: number
  attachments: number
  error: string | null
}

/** This device's transfer, if any: one at a time, sending or receiving. */
export const transfer = reactive<TransferProgress>({ role: null, phase: 'waiting', done: 0, total: 0, messages: 0, attachments: 0, error: null })

/** Attachments a transfer didn't bring (MediaView says so once the server no longer has them). */
export const notTransferred = reactive(new Set<string>())
/** Attachments a transfer brought while their messages were already showing (MediaView looks again). */
export const arrived = reactive(new Set<string>())

export function transferRunning(): boolean {
  return transfer.role !== null && ['waiting', 'history', 'attachments'].includes(transfer.phase)
}

let stopper: AbortController | null = null

// Closing the page mid-transfer stops it: ask first.
globalThis.addEventListener?.('beforeunload', (e) => {
  if (transferRunning() && transfer.phase !== 'waiting') e.preventDefault()
})

/** Stops this device's transfer (the parts already on the server expire by themselves). */
export function stopTransfer(): void {
  stopper?.abort()
  if (transferRunning()) transfer.phase = 'stopped'
}

/** Where a transfer reports to: this device's `transfer` (stopped by `stopTransfer`) unless given. */
export interface Track {
  progress?: TransferProgress
  signal?: AbortSignal
}

function begin(role: 'send' | 'receive', track: Track): { signal: AbortSignal; p: TransferProgress } {
  let signal = track.signal
  if (!track.progress) {
    stopper?.abort()
    stopper = new AbortController()
    signal = stopper.signal
  }
  const p = track.progress ?? transfer
  Object.assign(p, { role, phase: 'waiting', done: 0, total: 0, messages: 0, attachments: 0, error: null })
  return { signal: signal ?? new AbortController().signal, p }
}

const AAD = new TextEncoder().encode('hrth-transfer:v1')

/** A fresh transfer key (32 bytes, base64url) for a link's fragment. */
export function newTransferKey(): string {
  return b64url(crypto.getRandomValues(new Uint8Array(32)))
}

async function aes(key: string, usage: KeyUsage): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', fromB64url(key), 'AES-GCM', false, [usage])
}

export async function sealMeta(key: string, meta: TransferMeta): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: AAD }, await aes(key, 'encrypt'), new TextEncoder().encode(JSON.stringify(meta)))
  const out = new Uint8Array(12 + ct.byteLength)
  out.set(iv)
  out.set(new Uint8Array(ct), 12)
  return b64url(out)
}

/** The label, or null if it isn't sealed under this key. */
export async function openMeta(key: string, sealed: string): Promise<TransferMeta | null> {
  try {
    const raw = fromB64url(sealed)
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: raw.slice(0, 12), additionalData: AAD }, await aes(key, 'decrypt'), raw.slice(12))
    return JSON.parse(new TextDecoder().decode(pt)) as TransferMeta
  } catch {
    return null
  }
}

/** The attachments a set of messages refers to, oldest first, each once. */
export function attachmentIds(messages: StoredMessage[]): string[] {
  const ids = new Set<string>()
  for (const m of messages) if (m.envelope.type === 'media') ids.add(m.envelope.media.id)
  return [...ids]
}

/** What a transfer would send: message count, and the attachments this device keeps (for "about N MB"). */
export async function estimate(source: TransferSource): Promise<{ messages: number; attachments: number; bytes: number }> {
  const messages = await source.messages()
  let attachments = 0
  let bytes = 0
  for (const id of attachmentIds(messages)) {
    const f = await source.attachment(id)
    if (f) {
      attachments++
      bytes += f.size
    }
  }
  return { messages: messages.length, attachments, bytes }
}

export interface Timing {
  /** Between polls of the server (ms). */
  poll?: number
  wait?: (ms: number) => Promise<void>
  now?: () => number
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/** Thrown once stopped; its 4xx status makes a resumable upload cancel rather than retry. */
class Stopped extends Error {
  status = 499
}

function aborted(signal: AbortSignal): void {
  if (signal.aborted) throw new Stopped('stopped')
}

/** Uploads one part; waits while the server holds as much as it will for the other device. */
async function sendPart(
  api: TransferApi,
  to: string,
  key: string,
  file: Blob,
  label: (info: MediaKeyInfo) => TransferMeta,
  signal: AbortSignal,
  t: Required<Timing>,
  onProgress: (sent: number) => void,
): Promise<void> {
  const enc = await fileEncryptor(file)
  const meta = await sealMeta(key, label(enc.info))
  await uploadResumable(
    {
      startUpload: async (size) => {
        for (;;) {
          aborted(signal)
          try {
            return await api.startTransfer(to, size, meta)
          } catch (e) {
            if ((e as { status?: number }).status !== 429) throw e
            await t.wait(t.poll)
          }
        }
      },
      uploadPart: (id, offset, part) => {
        aborted(signal)
        return api.transferPart(id, offset, part)
      },
      uploadStatus: (id) => api.transferStatus(id),
      completeUpload: async (id) => {
        await api.completeTransfer(id)
        return { id, size: file.size }
      },
      cancelUpload: (id) => api.deleteTransfer(id),
    },
    enc,
    { onProgress: (f) => onProgress(f * file.size), wait: t.wait },
  )
}

/**
 * The old device: waits (up to `waitFor` ms) for the new device to say it's ready, then
 * sends history and, with `attachments`, the attachments it keeps for those messages.
 */
export async function sendHistory(
  api: TransferApi,
  key: string,
  source: TransferSource,
  opts: { attachments: boolean; waitFor?: number } & Timing & Track,
): Promise<void> {
  const { signal, p } = begin('send', opts)
  const t = { poll: opts.poll ?? 2000, wait: opts.wait ?? sleep, now: opts.now ?? Date.now }
  const until = t.now() + (opts.waitFor ?? 20 * 60_000)
  try {
    let to: string | null = null
    while (!to) {
      aborted(signal)
      if (t.now() > until) throw new Error('The new device didn’t ask for your history in time.')
      for (const part of (await api.waitingTransfers()).parts) {
        const meta = await openMeta(key, part.meta)
        if (meta?.kind !== 'ready') continue
        to = part.from
        await api.deleteTransfer(part.id).catch(() => undefined)
      }
      if (!to) await t.wait(t.poll)
    }

    const messages = await source.messages()
    const ids = attachmentIds(messages)
    const files: [string, Blob][] = []
    if (opts.attachments) {
      for (const id of ids) {
        const f = await source.attachment(id)
        if (f) files.push([id, f])
      }
    }
    const sending = new Set(files.map(([id]) => id))
    const doc: HistoryDoc = {
      format: 'hrth-transfer/v1',
      messages,
      attachments: [...sending],
      bytes: files.reduce((n, [, f]) => n + f.size, 0),
      skipped: ids.filter((id) => !sending.has(id)),
    }
    const history = new Blob([JSON.stringify(doc)], { type: 'application/json' })
    Object.assign(p, { phase: 'history', total: history.size + doc.bytes, messages: messages.length })
    await sendPart(api, to, key, history, (info) => ({ kind: 'history', info }), signal, t, (n) => (p.done = n))

    p.phase = 'attachments'
    let base = history.size
    for (const [id, file] of files) {
      try {
        await sendPart(api, to, key, file, (info) => ({ kind: 'attachment', info, media: id, mime: file.type }), signal, t, (n) => (p.done = base + n))
        p.attachments++
      } catch (e) {
        // One that won't go doesn't stop the rest; the new device shows it as not transferred.
        if (signal.aborted) throw e
      }
      base += file.size
      p.done = base
    }
    await api.startTransfer(to, 0, await sealMeta(key, { kind: 'done' }))
    p.phase = 'done'
  } catch (e) {
    if (signal.aborted) return
    p.phase = 'failed'
    p.error = e instanceof Error ? e.message : String(e)
  }
}

/**
 * The new device, once it has joined its conversations: asks `from` for its history and
 * takes in what arrives. Gives up when nothing has come for `idle` ms (a part waits on
 * the server 10 minutes at most).
 */
export async function receiveHistory(
  api: TransferApi,
  link: { key: string; from: string },
  sink: TransferSink,
  opts: { idle?: number } & Timing & Track = {},
): Promise<void> {
  const { signal, p: progress } = begin('receive', opts)
  const t = { poll: opts.poll ?? 2000, wait: opts.wait ?? sleep, now: opts.now ?? Date.now }
  const idle = opts.idle ?? 10 * 60_000
  let expected: string[] = []
  const got = new Set<string>()
  let history = false
  try {
    await api.startTransfer(link.from, 0, await sealMeta(link.key, { kind: 'ready' }))
    let last = t.now()
    for (;;) {
      aborted(signal)
      let finished = false
      for (const part of (await api.waitingTransfers()).parts) {
        if (part.from !== link.from) continue
        const meta = await openMeta(link.key, part.meta)
        if (!meta) continue
        last = t.now()
        if (meta.kind === 'history') {
          const plain = await decryptAll(meta.info, await api.transferData(part.id))
          const doc = JSON.parse(new TextDecoder().decode(plain)) as HistoryDoc
          if (doc.format !== 'hrth-transfer/v1' || !Array.isArray(doc.messages)) throw new Error('The history that arrived isn’t readable.')
          progress.phase = 'history'
          progress.messages = await sink.importMessages(doc.messages)
          await sink.notTransferred(doc.skipped ?? [])
          expected = doc.attachments ?? []
          history = true
          Object.assign(progress, { phase: expected.length ? 'attachments' : 'history', total: plain.byteLength + (doc.bytes ?? 0), done: plain.byteLength })
        } else if (meta.kind === 'attachment') {
          try {
            const plain = await decryptAll(meta.info, await api.transferData(part.id))
            await sink.keepAttachment(meta.media, new Blob([plain], { type: meta.mime }))
            got.add(meta.media)
            arrived.add(meta.media)
            progress.attachments = got.size
          } catch {
            // Shown as not transferred; the history is in already.
          }
          progress.done += meta.info.size
        } else if (meta.kind === 'done') {
          finished = true
        }
        await api.deleteTransfer(part.id).catch(() => undefined)
      }
      if (finished) break
      if (t.now() - last > idle) throw new Error(history ? 'Some attachments didn’t arrive.' : 'Your other device didn’t send your history in time.')
      await t.wait(t.poll)
    }
    progress.phase = 'done'
  } catch (e) {
    if (signal.aborted) return
    progress.phase = 'failed'
    progress.error = e instanceof Error ? e.message : String(e)
  } finally {
    const missing = expected.filter((id) => !got.has(id))
    if (missing.length) await sink.notTransferred(missing).catch(() => undefined)
  }
}
